import React, { memo, useContext, useEffect } from 'react'
import { TrackingContextCard } from 'components/TrackingContextCard/TrackingContextCard'
import { Assignee, ApptWarning, ApptActive } from 'components/atoms/icons'
import { StopSummary } from 'components/marketplace/StopSummary'
import moment from 'moment-timezone'
import PropTypes from 'prop-types'
import { Avatar } from 'transfix-ui/components/Avatar'
import { Button } from 'transfix-ui/components/Button'
import { Checkbox } from 'transfix-ui/components/Checkbox'
import { InlineMessage, INLINE_MESSAGE_VARIANTS } from 'transfix-ui/components/InlineMessage'
import { ManagerTag } from 'transfix-ui/components/ManagerTag'
import { Tooltip } from 'transfix-ui/components/Tooltip'
import { Box } from 'transfix-ui/core'
import { FeatureFlagsContext } from 'utils/context/FeatureFlagsContext'
import { RateSourcingTooltip } from 'apps/marketplace/components'
import { OpenIssues } from 'apps/marketplace/components/Issues'
import getEmailSchedulingData from 'apps/marketplace/pages/shipment-details/components/ShipmentProgress/StopExpandableRow/utils'
import {
  HotIcon,
  GlobeIcon,
  RolledIcon,
  HazmatIcon,
  HighValueIcon,
  ContractFilledIcon,
  BackupOutlinedIcon,
  BackhaulIcon,
  MustGoIcon,
  CheckFilledIcon,
  RemoveIcon,
  SameDaySpotIcon,
  TrueRatePlusFilledIcon,
} from 'transfix-icons'
import { isShipmentFlagEnabled } from 'utils/accessorialHelpers'
import { displayAutoApptStatus } from 'utils/appointmentHelpers'
import { EquipmentTypeIcon } from 'utils/equipment'
import { formatCurrency } from 'utils/formatCurrency'
import { companyNameInitials } from 'utils/initials'
import { stopsSummary } from 'utils/shipmentHelpers'
import { DISPLAY } from 'constants/display'
import { DEFAULT_DROP_TRAILER } from 'constants/equipment'
import { HIGH_DECLARED_VALUE_AMOUNT } from 'constants/shipments'
import {
  IN_TRANSIT_STATUSES,
  ASSIGNED_SHIPMENT_STATUS,
  TENDERED_SHIPMENT_STATUS,
  PENDING_SHIPMENT_STATUSES,
  SUBMITTED_STATUSES,
  ON_HOLD_STATUS,
  POST_TRANSIT_STATUSES,
  ARCHIVED_STATUSES,
  TRACKING_STATUSES,
  REDACTED_CARRIER_STATUSES,
  SUBMITTED_TAB_LABEL,
} from '../../constants/shipments'
import { setDropTrailerStatus } from '../../utils/setDropTrailerStatus'
import { getStopUpdatedInfo } from '../../utils/stopUpdatedConfirmedUtils'
import { useShipmentRateSourceInfoLazyQuery } from './graphql/ShipmentRateSourceInfo'

const RowCheckbox = memo(({ disabled, isChecked, onChange, showCheckbox }) => {
  if (!showCheckbox) return null
  const changeHandler = ({ target: { checked } }) => onChange(checked)

  return (
    <div className='col-lg-1'>
      <Checkbox disabled={disabled} checked={isChecked} onChange={changeHandler} />
    </div>
  )
})

const GeneralInfo = memo(({ shipment, sidebarOpen }) => {
  if (sidebarOpen) return null
  const {
    equipment,
    flags,
    hasBackhaulEligibleLane,
    hasHazmat,
    id,
    info,
    pricingType,
    rates,
    users,
  } = shipment
  const { icon, id: equipmentId, name } = equipment
  const { shipperName } = users
  const shipper = <span data-tooltip={shipperName}>{companyNameInitials(shipperName, 3)}</span>

  const shipmentLink = (
    <a href={`${__ROOT__}/shipment-details/${id}`} target='_blank' rel='noopener noreferrer'>
      #{id}
    </a>
  )
  const mustGo = isShipmentFlagEnabled(flags, 'must_go')
  const sameDaySpot = isShipmentFlagEnabled(flags, 'same_day_spot')
  const isHighValue =
    info?.declaredValue && parseFloat(info?.declaredValue) >= HIGH_DECLARED_VALUE_AMOUNT

  return (
    <Box className='col-lg-2'>
      <Box display='grid' gridTemplateColumns='3fr 1fr'>
        <Box
          data-test='booking-row-shipper-name'
          display='flex'
          alignItems='center'
          flexWrap='wrap'>
          <span className='no-shrink'>
            <EquipmentTypeIcon
              className='icon-secondary large inline-left mt1'
              tooltip={name}
              id={equipmentId}
              icon={icon}
            />
          </span>
          <span className='mr1'>
            {shipper}&nbsp;{shipmentLink}
          </span>
          <span className='mt1'>
            {hasHazmat && (
              <span className='no-shrink'>
                <Tooltip position='top' id='hazmat' trigger={<HazmatIcon />}>
                  Hazmat
                </Tooltip>
              </span>
            )}
            {rates.rateType === 'Contract' && (
              <span className='no-shrink'>
                <Tooltip
                  position='top'
                  id='contract'
                  trigger={
                    <span>
                      <ContractFilledIcon size='large' color='coolGray5' />
                    </span>
                  }>
                  Contract
                </Tooltip>
              </span>
            )}
            {sameDaySpot && (
              <span className='no-shrink'>
                <Tooltip
                  position='top'
                  id='same-day-spot'
                  trigger={
                    <span>
                      <SameDaySpotIcon className='large icon-secondary' />
                    </span>
                  }>
                  Same Day Spot
                </Tooltip>
              </span>
            )}
            {rates.rateType === 'Backup' && (
              <span className='no-shrink'>
                <Tooltip
                  position='top'
                  id='backup'
                  trigger={
                    <span>
                      <BackupOutlinedIcon size='large' color='coolGray5' />
                    </span>
                  }>
                  Backup
                </Tooltip>
              </span>
            )}
            {pricingType === 'variable' && (
              <span className='no-shrink'>
                <Tooltip
                  position='top'
                  id='trueRate'
                  trigger={
                    <span>
                      <TrueRatePlusFilledIcon size='large' color='coolGray8' />
                    </span>
                  }>
                  TrueRate+
                </Tooltip>
              </span>
            )}
            {info.hot && (
              <span className='no-shrink'>
                <Tooltip
                  position='top'
                  id='hot'
                  trigger={
                    <span>
                      <HotIcon size='large' />
                    </span>
                  }>
                  Hot
                </Tooltip>
              </span>
            )}

            {mustGo && (
              <Tooltip
                position='top'
                trigger={<MustGoIcon color='error' className='icon-special' size='large' />}>
                Must Go
              </Tooltip>
            )}
            {hasBackhaulEligibleLane && (
              <span className='no-shrink'>
                <Tooltip
                  position='top'
                  id='backHaul'
                  trigger={
                    <span>
                      <BackhaulIcon size='large' />
                    </span>
                  }>
                  Backhaul Eligible
                </Tooltip>
              </span>
            )}
            {info.rolloverCount > 0 && (
              <span className='no-shrink'>
                <Tooltip
                  position='top'
                  id='rollover'
                  trigger={
                    <span>
                      <RolledIcon size='large' color='error' />
                    </span>
                  }>
                  RolledIcon {info.rolloverCount}x
                </Tooltip>
              </span>
            )}
            {isHighValue && (
              <span className='no-shrink'>
                <Tooltip
                  position='top'
                  id='high-value'
                  trigger={
                    <span>
                      <HighValueIcon size='large' />
                    </span>
                  }>
                  High Value - Over $100k
                </Tooltip>
              </span>
            )}
          </span>
        </Box>
        <OpenIssues shipment={shipment} />
      </Box>
    </Box>
  )
})

const Rates = memo(({ displayStrategicBuyRate, rates, sidebarOpen, status, users }) => {
  const { getFlag } = useContext(FeatureFlagsContext)
  const { enabled: maskCmtRates } = getFlag('maskCmtRates')
  const bookableStatuses = TENDERED_SHIPMENT_STATUS
  const bookable = bookableStatuses.includes(status)
  const reserved = ['waterfall_offered', 'reserved'].includes(status)

  const ceilingText = maskCmtRates ? 'List Price' : 'CM Target'

  let carrierCeilingPrice = DISPLAY.empty
  if (!maskCmtRates && rates.tcmCarrierPrices?.cmTarget?.amount) {
    carrierCeilingPrice = rates.tcmCarrierPrices.cmTarget.amount
  } else if (rates.tcmCarrierPrices?.listPrice) {
    carrierCeilingPrice = rates.tcmCarrierPrices.listPrice
  }

  const rate = reserved ? rates.carrierRate : carrierCeilingPrice
  const reservedWaiting = reserved && !users.carrierId

  if (bookable) {
    return (
      <>
        {!sidebarOpen && !reservedWaiting && (
          <div className='col-lg-3'>
            <p className='ellipsis'>
              {formatCurrency(rate, undefined, true)}
              {!reserved ? (
                <span className='text-secondary'>&nbsp;&nbsp;{ceilingText}</span>
              ) : (
                <span>
                  &nbsp;&middot;&nbsp;Reserved for&nbsp;
                  <a
                    href={`${__ROOT__}/carriers/${users.carrierId}`}
                    target='_blank'
                    rel='noopener noreferrer'>
                    {users.carrierName}
                  </a>
                </span>
              )}
            </p>
            {displayStrategicBuyRate && rates.strategicBuyRate && (
              <p className='ellipsis'>
                {formatCurrency(rates.strategicBuyRate, undefined, true)}
                <span className='text-secondary'>&nbsp;&nbsp;SBR</span>
              </p>
            )}
          </div>
        )}
        {reservedWaiting && (
          <div className='col-lg-3'>
            <p>Reserved</p>
          </div>
        )}
      </>
    )
  }
  return null
})

const CarrierText = memo(({ id, name, review, status }) => {
  if (review && status) {
    return <span>&nbsp;&middot;&nbsp;{status}</span>
  }
  if (id && name) {
    return (
      <a href={`${__ROOT__}/carriers/${id}`} target='_blank' rel='noopener noreferrer'>
        &nbsp;&middot;&nbsp;{name}
      </a>
    )
  }
  return <span>&nbsp;&middot;&nbsp;No Carrier</span>
})

const Carrier = ({
  carrierColumnClass,
  coveredBid,
  shipmentId,
  shipperTrackingSegment,
  sidebarOpen,
  status,
  trackingStatus,
  users,
}) => {
  const statuses = [
    ...IN_TRANSIT_STATUSES,
    ...ASSIGNED_SHIPMENT_STATUS,
    ...PENDING_SHIPMENT_STATUSES,
    ...SUBMITTED_STATUSES,
    ...POST_TRANSIT_STATUSES,
    ...ARCHIVED_STATUSES,
  ]
  const showCarrier = !REDACTED_CARRIER_STATUSES.includes(status.key)
  let carrierId
  let carrierName
  let complianceReview
  let complianceStatus
  if (coveredBid) {
    ;({ carrierId, carrierName, complianceReview, complianceStatus } = coveredBid)
  } else {
    ;({ carrierId, carrierName } = users)
  }
  const notBookable = statuses.includes(status.key)
  if (!notBookable || sidebarOpen) return null
  const showTracking = TRACKING_STATUSES.includes(status.key)

  return (
    <Box className={carrierColumnClass} onClick={e => e.stopPropagation()}>
      {showTracking && (
        <TrackingContextCard
          trackingStatus={trackingStatus}
          shipperTrackingSegment={shipperTrackingSegment}
          shipmentId={shipmentId}
          renderType='withBoundary'
          popperOptions={{ boundary: document.querySelector('.booking-container') }}
        />
      )}
      <p className='ellipsis'>
        {status.name}
        {showCarrier && (
          <CarrierText
            id={carrierId}
            review={complianceReview}
            status={complianceStatus}
            name={carrierName}
          />
        )}
      </p>
    </Box>
  )
}

const Users = memo(({ sidebarOpen, useCeRep, users }) => {
  if (sidebarOpen) return null

  const { ceRepName, opsRepName } = users
  const toolTip = useCeRep ? 'No CE Rep assigned' : 'No Ops Rep assigned'
  const showCeRep = useCeRep && ceRepName
  const fullName = useCeRep ? ceRepName : opsRepName
  const showOpsRep = useCeRep ? false : opsRepName
  const showManagerTag = !(!showOpsRep && !showCeRep)

  return (
    <>
      {showManagerTag && (
        <div className='col-lg-3'>
          <ManagerTag type='owner' fullName={fullName} variant='booking' align='right' />
        </div>
      )}
      {!showManagerTag && (
        <div className='ml1'>
          <Assignee tooltip={toolTip} margin='left top' />
        </div>
      )}
    </>
  )
})

const LastTouched = memo(({ info, showLastTouched, sidebarOpen }) => {
  if (sidebarOpen) return null
  if (!showLastTouched) return null
  const now = moment()
  const { lastActionAt, lastActionUpdatedByName } = info
  let { lastActionClass } = info
  if (lastActionClass === 'ShipmentStop') lastActionClass = 'ETA'
  if (lastActionClass === 'ShipmentStateTransition') lastActionClass = 'Status'
  const lastTouchedData = moment.duration(now.diff(lastActionAt))
  const { days, hours, minutes } = lastTouchedData._data
  const hoursAndMinutes = hours ? `${hours}h ${minutes}m ago` : `${minutes}m ago`
  const timeSinceLastTouched = days ? `${days}d ${hours}h ago` : hoursAndMinutes

  return (
    <div className='col-lg-7'>
      <span data-tooltip={`${lastActionClass} Updated by: ${lastActionUpdatedByName}`}>
        {timeSinceLastTouched}
      </span>
    </div>
  )
})

const LastNote = memo(({ id, info, showLastNote, sidebarOpen }) => {
  if (sidebarOpen) return null
  if (!showLastNote) return null
  const now = moment()
  const { lastNoteAt, lastNoteSnippet, lastNoteUpdatedByName } = info
  let { lastActionClass } = info
  if (lastActionClass === 'ShipmentStop') lastActionClass = 'ETA'
  if (lastActionClass === 'ShipmentStateTransition') lastActionClass = 'Status'
  const lastTouchedData = moment.duration(now.diff(lastNoteAt))
  const { days, hours, minutes } = lastTouchedData._data
  const hoursAndMinutes = hours ? `${hours}h ${minutes}m ago` : `${minutes}m ago`
  const timeSinceLastNote = days ? `${days}d ${hours}h ago` : hoursAndMinutes
  const showNote = !!lastNoteSnippet

  return (
    <div className='col-lg-9'>
      {showNote && (
        <Tooltip
          id={`${id}-last-note`}
          position='top'
          trigger={
            <span data-test='last-note' className='ellipsis-in-row'>
              {lastNoteSnippet}
            </span>
          }>
          {`Note added by ${lastNoteUpdatedByName} ${timeSinceLastNote}`}
        </Tooltip>
      )}
      {!showNote && <p>--</p>}
    </div>
  )
})

export const BookingRow = memo(
  ({
    acceptedSinceLastRefresh,
    activateRow,
    checkboxEnabled,
    currentTabName,
    isActive,
    onAcceptShipmentClick,
    onCheckboxChange,
    onRejectShipmentClick,
    rejectedSinceLastRefresh,
    rowIsChecked,
    shipment,
    sidebarOpen,
    tabIndex,
  }) => {
    const { getFlag } = useContext(FeatureFlagsContext)
    const { enabled: redirectToNewShipmentDetailsEnabled } = getFlag('redirectToNewShipmentDetails')
    const { enabled: displayStrategicBuyRate } = getFlag('displayStrategicBuyRate')
    const { enabled: shipperRateSourcingEnabled } = getFlag('shipperRateSourcing')
    const { enabled: mpEmailSchedulingVisibilityEnabled } = getFlag('mpEmailSchedulingVisibility')

    const {
      coveredBid,
      id,
      info,
      rates,
      shipperMetadata,
      shipperTrackingSegment,
      status,
      trackingStatus,
      users,
    } = shipment
    const { trackingRequired } = info
    const { delivery, pickup } = stopsSummary(shipment)
    const { shipperRate, targetMarginRate } = rates
    const totalNumPickups = shipment.pickups.length
    const totalNumDeliveries = shipment.deliveries.length
    const { autoAcceptEnabled = false, canRespondToTender = false } = shipperMetadata || {}

    const changedSinceLastRefresh = acceptedSinceLastRefresh || rejectedSinceLastRefresh

    const rowClass = `row row-clickable ${isActive ? 'active' : ''}`
    const mobileView = window.screen.width < 786
    const handleRowSelection = e => {
      if (e.target && e.target.tagName.toLowerCase() === 'a') {
        return true
      }
      if (e.target && e.target.tagName.toLowerCase() === 'input') {
        return true
      }
      if (e.key === 'ENTER' || e.type === 'click') {
        const shipmentDetailsLink = `${__ROOT__}/shipment-details/${id}?tab=scheduling`

        if (mobileView) return window.open(shipmentDetailsLink, '_blank')
        activateRow()
      }
      return true
    }
    const handleCheckboxChange = isChecked => {
      onCheckboxChange(id, isChecked)
    }
    const showLastTouched = [...ASSIGNED_SHIPMENT_STATUS, ...IN_TRANSIT_STATUSES].includes(
      status.key
    )
    const showApptSchedulingInfo = ON_HOLD_STATUS.includes(status.key)
    const showCheckbox = currentTabName === SUBMITTED_TAB_LABEL
    const getLastColumnClass = () => {
      if (showLastTouched) return 'col-lg-2'
      if (showApptSchedulingInfo) return 'col-lg-4'
      return 'col-lg-1'
    }
    const lastColumnClass = getLastColumnClass()
    const carrierColumnClass = showLastTouched
      ? 'col-lg-2 flex items-center'
      : 'col-lg-3 flex items-center'
    const {
      managerTagTooltip: managerTagTooltipPickup,
      showScheduleByWebIcon: showScheduleByWebIconPickup,
      updatedByName: updatedByNamePickup,
    } = getStopUpdatedInfo(pickup)
    const {
      managerTagTooltip: managerTagTooltipDelivery,
      showScheduleByWebIcon: showScheduleByWebIconDelivery,
      updatedByName: updatedByNameDelivery,
    } = getStopUpdatedInfo(delivery)
    const { delivery: dropTrailerDelivery, pickup: dropTrailerPickup } = setDropTrailerStatus(
      shipment
    )
    const { name: equipmentName } = DEFAULT_DROP_TRAILER

    const summaryIcon = () => (
      <EquipmentTypeIcon
        className='icon-secondary large'
        icon={DEFAULT_DROP_TRAILER.icon}
        tooltip={equipmentName}
      />
    )
    const webIcon = () => (
      <Tooltip
        position='top'
        id='globe'
        trigger={
          <span>
            <GlobeIcon color='coolGray5' />
          </span>
        }>
        Schedule by Web / TMS
      </Tooltip>
    )

    const pickupManagerTag = () => (
      <ManagerTag
        id='manager'
        type='owner'
        variant='custom-tooltip'
        fullName={updatedByNamePickup}
        customTooltip={managerTagTooltipPickup}
      />
    )
    const deliveryManagerTag = () => (
      <ManagerTag
        id='manager'
        type='owner'
        variant='custom-tooltip'
        fullName={updatedByNameDelivery}
        customTooltip={managerTagTooltipDelivery}
      />
    )
    const apptIcons = {
      unconfirmed: message => <ApptActive tooltip={message} />,
      notResponding: message => <ApptWarning tooltip={message} />,
    }

    const [pIcon, pApptText] = displayAutoApptStatus(
      pickup.status,
      pickup.autoAppointment,
      pickup.facilityHasContacts,
      pickup.canAutoEmail,
      pickup.appointmentTimeUpdatedByName
    )
    const [dIcon, dApptText] = displayAutoApptStatus(
      delivery.status,
      delivery.autoAppointment,
      delivery.facilityHasContacts,
      delivery.canAutoEmail,
      delivery.appointmentTimeUpdatedByName
    )

    const pickupBadge = getEmailSchedulingData(
      pickup.autoAppointment
        ? {
            createdAt: pickup.autoAppointment.createdAt,
            createdBy: null,
            source: pickup.autoAppointment.source,
            lastRequestedAt: pickup.autoAppointment.lastRequestedAt,
            requestedCount: pickup.autoAppointment.requestedCount,
          }
        : null,
      pickup.smartAppointmentEligible,
      {
        status: pickup.status,
        timeUpdatedBy: {
          fullName: pickup.appointmentTimeUpdatedByName,
          id: pickup.appointmentTimeUpdatedByName,
        },
      },
      mpEmailSchedulingVisibilityEnabled
    )

    const deliveryBadge = getEmailSchedulingData(
      delivery.autoAppointment
        ? {
            createdAt: delivery.autoAppointment.createdAt,
            createdBy: null,
            source: delivery.autoAppointment.source,
            lastRequestedAt: delivery.autoAppointment.lastRequestedAt,
            requestedCount: delivery.autoAppointment.requestedCount,
          }
        : null,
      delivery.smartAppointmentEligible,
      {
        status: delivery.status,
        timeUpdatedBy: {
          fullName: delivery.appointmentTimeUpdatedByName,
          id: delivery.appointmentTimeUpdatedByName,
        },
      },
      mpEmailSchedulingVisibilityEnabled
    )

    const pOldIconBadge = pIcon && pApptText ? apptIcons[pIcon](pApptText) : null
    const dOldIconBadge = dIcon && dApptText ? apptIcons[dIcon](dApptText) : null
    const pBadge = pickupBadge ? (
      <Tooltip id={`p${id}-email`} trigger={pickupBadge.icon.component}>
        {pickupBadge.icon.description}
      </Tooltip>
    ) : null
    const dBadge = deliveryBadge ? (
      <Tooltip id={`d${id}-email`} trigger={deliveryBadge.icon.component}>
        {deliveryBadge.icon.description}
      </Tooltip>
    ) : null

    const pickupEmailSchedulingBadge = mpEmailSchedulingVisibilityEnabled ? pBadge : pOldIconBadge
    const deliveryEmailSchedulingBadge = mpEmailSchedulingVisibilityEnabled ? dBadge : dOldIconBadge

    const pickupIcons = [
      dropTrailerPickup ? summaryIcon() : null,
      showApptSchedulingInfo && showScheduleByWebIconPickup ? webIcon() : null,
      showApptSchedulingInfo && updatedByNamePickup ? pickupManagerTag() : null,
      pickupEmailSchedulingBadge,
    ].filter(Boolean) // filters out nulls

    const deliveryIcons = [
      dropTrailerDelivery ? summaryIcon() : null,
      showApptSchedulingInfo && showScheduleByWebIconDelivery ? webIcon() : null,
      showApptSchedulingInfo && updatedByNameDelivery ? deliveryManagerTag() : null,
      deliveryEmailSchedulingBadge,
    ].filter(Boolean)

    const onAcceptHandler = e => {
      // Stop event propagation to prevent
      // the sidebar from opening:
      e.stopPropagation()
      onAcceptShipmentClick(id)
    }
    const onRejectHandler = e => {
      // Stop event propagation to prevent
      // the sidebar from opening:
      e.stopPropagation()
      onRejectShipmentClick(id)
    }
    const stopSummaryColumnClass = (() => {
      // When the sidebar is open, only the stop summary
      // columns are visible so they should each take
      // half the table width:
      if (sidebarOpen) return 'col-lg-6'
      // The submitted tab has extra columns for shipper rate
      // and target margin, so stop summaries need to be smaller:
      if (currentTabName === SUBMITTED_TAB_LABEL) return 'col-lg-2'
      return 'col-lg-3'
    })()
    const formatRate = rateStr => {
      if (typeof rateStr !== 'string') return DISPLAY.empty
      const rate = Number(rateStr)
      if (Number.isNaN(rate)) return DISPLAY.empty
      return `$${rate.toFixed(2)}`
    }

    const [getShipmentRateSourceInfo, { data, loading }] = useShipmentRateSourceInfoLazyQuery({
      variables: {
        id,
      },
    })
    const rateSources = data?.shipment?.shipperRateSources || []

    useEffect(() => {
      if (shipperRateSourcingEnabled && currentTabName === SUBMITTED_TAB_LABEL) {
        getShipmentRateSourceInfo()
      }
    }, [currentTabName, getShipmentRateSourceInfo, shipperRateSourcingEnabled])

    return (
      <>
        <>
          <div
            role='button'
            data-test={`booking-row-${tabIndex}`}
            className={rowClass}
            onKeyDown={handleRowSelection}
            onClick={handleRowSelection}
            tabIndex={0}>
            <RowCheckbox
              showCheckbox={showCheckbox}
              disabled={!checkboxEnabled}
              isChecked={rowIsChecked}
              onChange={handleCheckboxChange}
            />
            <GeneralInfo
              shipment={shipment}
              sidebarOpen={sidebarOpen}
              redirectToNewShipmentDetailsEnabled={redirectToNewShipmentDetailsEnabled}
            />
            <div className={stopSummaryColumnClass}>
              <StopSummary
                stop={pickup}
                totalNumStops={totalNumPickups}
                variation='inline'
                icons={pickupIcons}
              />
            </div>
            <div className={stopSummaryColumnClass}>
              <StopSummary
                stop={delivery}
                totalNumStops={totalNumDeliveries}
                variation='inline'
                icons={deliveryIcons}
              />
            </div>
            <Rates
              displayStrategicBuyRate={displayStrategicBuyRate}
              rates={rates}
              status={status.key}
              sidebarOpen={sidebarOpen}
              users={users}
            />
            {currentTabName === SUBMITTED_TAB_LABEL && !sidebarOpen ? (
              <>
                <div className='col-lg-1'>
                  <span className='text-secondary'>Ship </span>
                  <Box
                    display='inline-flex'
                    alignContent='center'
                    alignItems='center'
                    columnGap={1}>
                    {formatRate(shipperRate)}
                    <RateSourcingTooltip
                      rateSources={rateSources}
                      iconSize='large'
                      loading={loading}
                    />
                  </Box>
                </div>
                <div className='col-lg-2'>
                  <span className='text-secondary'>Tgt Marg</span> {formatRate(targetMarginRate)}
                </div>
                <div className='col-lg-2 tr'>
                  {changedSinceLastRefresh ? (
                    <>
                      {acceptedSinceLastRefresh && (
                        <InlineMessage
                          type={INLINE_MESSAGE_VARIANTS.INFO}
                          message='Shipment accepted.'
                        />
                      )}
                      {rejectedSinceLastRefresh && (
                        <InlineMessage
                          type={INLINE_MESSAGE_VARIANTS.WARNING}
                          message='Shipment rejected.'
                        />
                      )}
                    </>
                  ) : (
                    <Box display='inline-block'>
                      <Box display='flex' alignItems='center'>
                        {canRespondToTender && (
                          <Box mr={1}>
                            <Avatar
                              name='Tender Tracking'
                              tooltipName='Tender Tracking Logic in Place'
                              size='xsmall'
                              colorClassName={0}
                            />
                          </Box>
                        )}
                        {autoAcceptEnabled && (
                          <Box mr={1}>
                            <Avatar
                              name='Automated Response'
                              tooltipName='Automated Response to Tender'
                              size='xsmall'
                              colorClassName={6}
                            />
                          </Box>
                        )}
                        <Box ml={1}>
                          <Tooltip
                            position='top'
                            trigger={
                              <Button color='tertiary' onClick={onRejectHandler}>
                                <RemoveIcon />
                              </Button>
                            }>
                            Reject
                          </Tooltip>
                          <Tooltip
                            position='top'
                            trigger={
                              <Button color='primary' onClick={onAcceptHandler}>
                                <CheckFilledIcon />
                              </Button>
                            }>
                            Accept
                          </Tooltip>
                        </Box>
                      </Box>
                    </Box>
                  )}
                </div>
              </>
            ) : (
              <>
                <Carrier
                  shipmentId={id}
                  coveredBid={coveredBid}
                  status={status}
                  trackingRequired={trackingRequired}
                  users={users}
                  sidebarOpen={sidebarOpen}
                  carrierColumnClass={carrierColumnClass}
                  trackingStatus={trackingStatus}
                  shipperTrackingSegment={shipperTrackingSegment}
                />
                <div className={lastColumnClass}>
                  <div className='row collapse'>
                    <LastNote
                      id={String(id)}
                      sidebarOpen={sidebarOpen}
                      info={info}
                      status={status}
                      showLastNote={showApptSchedulingInfo}
                    />
                    <LastTouched
                      sidebarOpen={sidebarOpen}
                      info={info}
                      status={status}
                      showLastTouched={showLastTouched}
                    />
                    <Users sidebarOpen={sidebarOpen} users={users} useCeRep={showLastTouched} />
                  </div>
                </div>
              </>
            )}
          </div>
        </>
        <hr className='cf ma0' />
      </>
    )
  }
)

const sidebarOpenPropTypes = { sidebarOpen: PropTypes.bool }
const sidebarOpenDefaultProps = { sidebarOpen: false }

GeneralInfo.propTypes = {
  shipment: PropTypes.shape({}),
  ...sidebarOpenPropTypes,
}

GeneralInfo.defaultProps = {
  shipment: {
    id: 0,
    info: {},
    users: {},
    rates: {},
  },
  ...sidebarOpenDefaultProps,
}

Carrier.propTypes = {
  ...sidebarOpenPropTypes,
  coveredBid: PropTypes.shape({}),
  users: PropTypes.shape({
    carrierName: PropTypes.string,
    carrierId: PropTypes.number,
  }).isRequired,
  status: PropTypes.shape({
    key: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  }).isRequired,
  shipment: PropTypes.shape({}),
  carrierColumnClass: PropTypes.string.isRequired,
}
Carrier.defaultProps = {
  ...sidebarOpenDefaultProps,
  shipmentId: PropTypes.number,
}

Rates.propTypes = {
  ...sidebarOpenPropTypes,
  rates: PropTypes.shape({
    opsRepName: PropTypes.string,
  }).isRequired,
  users: PropTypes.shape({}).isRequired,
  status: PropTypes.string.isRequired,
}
Rates.defaultProps = {
  ...sidebarOpenDefaultProps,
}

Users.propTypes = {
  ...sidebarOpenPropTypes,
  users: PropTypes.shape({
    opsRepName: PropTypes.string,
  }),
  useCeRep: PropTypes.bool.isRequired,
}
Users.defaultProps = {
  users: { opsRepName: '' },
  ...sidebarOpenDefaultProps,
}

LastTouched.propTypes = {
  ...sidebarOpenPropTypes,
  showLastTouched: PropTypes.bool.isRequired,
}
LastTouched.defaultProps = {
  ...sidebarOpenPropTypes,
}

BookingRow.propTypes = {
  shipment: PropTypes.shape({}),
  activateRow: PropTypes.func.isRequired,
  tabIndex: PropTypes.number,
  isActive: PropTypes.bool,
  ...sidebarOpenPropTypes,
}

BookingRow.defaultProps = {
  shipment: {
    id: 0,
    info: {},
    users: {},
    pickups: [],
    deliveries: [],
  },
  tabIndex: 0,
  isActive: false,
  ...sidebarOpenPropTypes,
}

CarrierText.propTypes = {
  id: PropTypes.number,
  status: PropTypes.string,
  name: PropTypes.string,
  review: PropTypes.shape({}),
}

CarrierText.defaultProps = {
  id: null,
  status: null,
  name: null,
  review: null,
}

LastNote.propTypes = {
  info: PropTypes.shape({}).isRequired,
  sidebarOpen: PropTypes.bool.isRequired,
  showLastNote: PropTypes.bool.isRequired,
  id: PropTypes.string.isRequired,
}
